import { graphql } from "babel-plugin-relay/macro";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { VideoSourceField } from "./VideoSourceField";
import { ValidatedField } from "../../../../../../core/form/ValidatedField";
import { DefaultTextEditorComponent } from "../../../../../../core/form/DefaultTextEditorComponent";
import { VideoElementForm_EditVideoElementMutation } from "../../../../../../../../__generated__/VideoElementForm_EditVideoElementMutation.graphql";
import { useTypedDispatch } from "../../../../../../../redux/Store";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../../../../redux/slices/CoreSlice";
import { VideoElementForm_VideoElementFragment$key } from "../../../../../../../../__generated__/VideoElementForm_VideoElementFragment.graphql";
import { VideoElementForm_VimeoSourceQuery } from "../../../../../../../../__generated__/VideoElementForm_VimeoSourceQuery.graphql";

const VIMEO_SOURCE_QUERY = graphql`
	query VideoElementForm_VimeoSourceQuery {
		Admin {
			VimeoVideoSource {
				GetDefaultVimeoVideoSource {
					id
				}
			}
		}
	}
`;

const VIDEO_ELEMENT_FRAGMENT = graphql`
	fragment VideoElementForm_VideoElementFragment on VideoElement {
		id
		title
		vimeoId
	}
`;

const EDIT_VIDEO_ELEMENT_MUTATION = graphql`
	mutation VideoElementForm_EditVideoElementMutation($input: EditVideoElementV2Input!) {
		Admin {
			ElearningV2 {
				editVideoElement(input: $input) {
					videoElement {
						...VideoElementForm_VideoElementFragment
					}
				}
			}
		}
	}
`;

interface FormState {
	title: string;
	vimeoId?: string;
}

interface OwnProps {
	videoElementFragmentRef: VideoElementForm_VideoElementFragment$key;
	onBack: () => void;
}

export const VideoElementForm = ({ videoElementFragmentRef, onBack }: OwnProps) => {
	const {
		Admin: {
			VimeoVideoSource: { GetDefaultVimeoVideoSource: vimeoVideoSource },
		},
	} = useLazyLoadQuery<VideoElementForm_VimeoSourceQuery>(
		VIMEO_SOURCE_QUERY,
		{},
		{ fetchPolicy: "network-only" },
	);
	const element = useFragment<VideoElementForm_VideoElementFragment$key>(
		VIDEO_ELEMENT_FRAGMENT,
		videoElementFragmentRef,
	);
	const [editVideoElement, isEditingVideoElement] =
		useMutation<VideoElementForm_EditVideoElementMutation>(EDIT_VIDEO_ELEMENT_MUTATION);

	const formId = "VideoElementForm";
	const dispatch = useTypedDispatch();
	const formik = useFormik<FormState>({
		initialValues: {
			title: element.title,
			vimeoId: element.vimeoId as string | undefined,
		},
		validationSchema: Yup.object().shape({
			title: Yup.string().required("Das Feld Titel wird benötigt."),
			vimeoId: Yup.string().required("Das Feld Video wird benötigt."),
		}),
		onSubmit: (values) => {
			editVideoElement({
				variables: {
					input: {
						videoElementId: element.id,
						title: values.title,
						vimeoId: values.vimeoId as string,
						vimeoVideoSourceId: vimeoVideoSource?.id!,
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
				},
			});
		},
	});

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<ValidatedField<FormState, string>
				name={"title"}
				label={"Titel"}
				component={DefaultTextEditorComponent}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				formikConfig={formik}
			/>

			<ValidatedField<FormState, string>
				name={"vimeoId"}
				label={"Video"}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({ fieldValue, updateField, onChange }) => {
					return (
						<VideoSourceField
							onChange={onChange}
							onUpdate={updateField}
							videoId={fieldValue}
							videoSourceId={vimeoVideoSource?.id!}
						/>
					);
				}}
				formikConfig={formik}
			/>

			<Button
				disabled={isEditingVideoElement || !formik.isValid}
				type="submit"
				label="Speichern"
				className="mt-2"
			/>

			<Button
				type="button"
				onClick={() => {
					onBack();
				}}
				label="Zurück"
				className="p-button-secondary mt-2"
			/>
		</form>
	);
};
